import React, { useCallback, useEffect, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { useConfig } from '../../defaultConfig';
import type { Account } from '../../generated-types';
import { useTransferAccountsContext } from '../../hooks/useAccountState';
import useTranslation from '../../hooks/useTranslation';
import { updatePayment } from '../../services/paymentService';
import { useSigningContext } from '../../signingContext/signingContext';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import { TransactionForm, TransactionSubmitWrapper } from '../TransactionForm';
import { StopPaymentButton } from '../shared/StopPaymentButton';
import TransferFields from '../transaction/Transfer/TransferFields';
import { SetAccountType, TransferActionType } from '../transaction/Transfer/form/actions';
import transferReducer from '../transaction/Transfer/form/reducer';
import { getFutureState, TransferState } from '../transaction/Transfer/form/state';
import useFormContext, { TransferFormContext } from '../transaction/Transfer/form/useFormContext';
import useValidate from '../transaction/Transfer/form/validate';
import useHasChanged from '../transaction/Transfer/hooks/useHasChanged';
import useSetNotesInfo from '../transaction/Transfer/hooks/useSetNotesInfo';
import FuturePaymentButtonWrapper from './FuturePaymentButtonWrapper';
import FuturePaymentSigningWrapper from './FuturePaymentSigningWrapper';
import FuturePaymentTitle from './FuturePaymentTitle';
import FuturePaymentWarningMessage from './FuturePaymentWarningMessage';
import mapUpdateTransferRequest from './utils/mapUpdateTransferRequest';

interface Props {
    futurePayment: FormattedFuturePayment;
    onClose: () => void;
    hidePaymentButton: { value: boolean; set: (value: boolean) => void };
    account?: Account;
}

const FuturePaymentDetailsTransfer: React.FC<Props> = ({ futurePayment, onClose, hidePaymentButton, account }) => {
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const { targetFromAccount } = useTransferAccountsContext();

    const [status, setStatus] = useState<'updating' | 'payNow' | null>(null);

    const doUpdate = useCallback(
        (transferState: TransferState) => {
            return updatePayment({ value: futurePayment.id }, mapUpdateTransferRequest()(transferState));
        },
        [futurePayment.id],
    );
    const { signingOperationPending, startSigning } = useSigningContext(doUpdate);
    const { handleChange, checkFormValidity, state } = useFormContext();

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if ((await checkFormValidity()) && account) {
            setStatus('updating');
            startSigning(state, ANALYTICS_CATEGORIES.TRANSFER);
        }
    };

    const onPayNow = useCallback(() => {
        handleChange({ type: TransferActionType.TRANSFER_DATE, payload: getZonedDate() });
        if (account) {
            setStatus('payNow');
            startSigning(
                {
                    ...state,
                    transferDate: getZonedDate(),
                },
                ANALYTICS_CATEGORIES.TRANSFER,
            );
        }
    }, [account, startSigning, handleChange, state]);

    useEffect(() => {
        handleChange({
            type: TransferActionType.ACCOUNT_SELECTED,
            payload: {
                setAccountType: SetAccountType.SET_FROM_ACCOUNT,
                accountNumber: futurePayment.fromAccount.accountNumber,
            },
        });
    }, [futurePayment.fromAccount.accountNumber, handleChange]);

    const { ownText, receiverText } = useSetNotesInfo(futurePayment.id, handleChange);
    const hasChange = useHasChanged(state, futurePayment, receiverText, ownText);

    const { canReject, canEdit: editAllowed } = futurePayment.permissions;
    const canEdit = editAllowed;

    return (
        <FuturePaymentSigningWrapper
            onClose={onClose}
            paymentDate={state.transferDate}
            fromAccountId={targetFromAccount?.accountId}
            paymentType={futurePayment.paymentType}
            status={status}
        >
            <FuturePaymentTitle
                futurePaymentType={futurePayment.paymentType}
                title={translated.futurePaymentTitle.transfer}
                useLabel
            />
            <TransactionForm className="future-payment-details__form" onSubmit={onSubmit}>
                <TransferFields
                    locked={!canEdit || signingOperationPending}
                    shouldFocusFirstElement
                    useDesktopPlaceholder={false}
                />
                <TransactionSubmitWrapper className="future-payment__button-row">
                    <FuturePaymentButtonWrapper>
                        <FuturePaymentButtonWrapper.FirstRow>
                            {!hidePaymentButton.value && (
                                <Button
                                    buttonType="negative"
                                    onClick={onClose}
                                    data-testid="cancel-update-future-transfer"
                                >
                                    {translated.cancel}
                                </Button>
                            )}
                            {canEdit && hasChange && (
                                <div className="future-payment-details__save-btn-container">
                                    <ButtonWithSpinner
                                        className="future-payment-details__save-btn"
                                        buttonType="positive"
                                        type="submit"
                                        showSpinner={signingOperationPending}
                                    >
                                        {translated.save}
                                    </ButtonWithSpinner>
                                </div>
                            )}
                            {canEdit && !hasChange && featureToggles.enablePayNow && !hidePaymentButton.value && (
                                <ButtonWithSpinner
                                    className="future-payment-details__pay-now-btn"
                                    buttonType="positive"
                                    showSpinner={signingOperationPending}
                                    onClick={onPayNow}
                                >
                                    {translated.transferNow}
                                </ButtonWithSpinner>
                            )}
                        </FuturePaymentButtonWrapper.FirstRow>

                        {canReject && !hasChange && (
                            <StopPaymentButton
                                onExpandDialog={hidePaymentButton.set}
                                paymentId={futurePayment.id}
                                paymentType={futurePayment.paymentType}
                                setStatusToStopping={() => setStatus(null)}
                            />
                        )}
                    </FuturePaymentButtonWrapper>
                </TransactionSubmitWrapper>
            </TransactionForm>
            {!canEdit && <FuturePaymentWarningMessage paymentType={futurePayment.paymentType} canReject={canReject} />}
        </FuturePaymentSigningWrapper>
    );
};

export default ({ futurePayment, ...rest }: Props) => (
    <TransferFormContext
        validateHook={useValidate}
        reducer={transferReducer}
        initialState={{ ...getFutureState(futurePayment) }}
    >
        <FuturePaymentDetailsTransfer futurePayment={futurePayment} {...rest} />
    </TransferFormContext>
);
